import { Interpolation, Theme } from '@emotion/react'
import { AdSlot } from '@mycujoo/ui'
import { AdSlotSizing } from '@mycujoo/ui/dist/types/AdSlot'
import { FC, Fragment, useEffect } from 'react'
import {
  useAnnotationsStore,
  useDispatch as useDispatchAnnotations,
} from '../../../contexts/annotations'
import { usePlayerState } from '../../../contexts/playerState'
import {
  OverlaysMap,
  parseOverlays,
  parseTimers,
  parseVariables,
} from '../../../utils/annotations'
import Overlay, { OverlayWrapper } from './Overlay'

const adSizing: AdSlotSizing[] = [
  {
    viewport: [0, 0],
    slot: ['fluid'],
  },
]

const Overlays: FC = () => {
  const {
    isInitialized,
    playerInstance,
    currentTime,
    totalTime,
    playerWidth,
    playerHeight,
  } = usePlayerState()
  const { overlays, preloadedImages, variables, timers } = useAnnotationsStore()
  const dispatchAnnotations = useDispatchAnnotations()

  useEffect(() => {
    if (isInitialized && playerInstance && overlays.length)
      overlays
        .filter((overlay) => overlay.type === 'show_overlay')
        .map((overlay) => {
          const imagePreFetch = async () => {
            const overlayID = overlay.data.custom_id || overlay.id

            if (
              overlay.type === 'show_overlay' &&
              overlay.data.svg_url?.includes('.svg')
            ) {
              try {
                const response = await fetch(overlay.data.svg_url)
                const contents = await response.text()

                dispatchAnnotations({
                  type: 'preloadOverlayImage',
                  overlayID,
                  contents,
                })
              } catch (error) {
                dispatchAnnotations({
                  type: 'preloadOverlayImage',
                  overlayID,
                  contents: '',
                })
              }
            }
          }

          try {
            // tslint:disable-next-line: no-floating-promises
            imagePreFetch()
          } catch (e) {
            console.error('Failed to fetch overlay image component', e)
          }
        })
  }, [isInitialized, playerInstance, overlays, dispatchAnnotations])

  if (!isInitialized || !playerInstance || !totalTime) return null

  const overlaysMap: OverlaysMap = parseOverlays(overlays, currentTime)

  const variablesMap = parseVariables(variables, currentTime)
  const timersMap = parseTimers(timers, currentTime)

  timersMap.forEach((value, key) => {
    variablesMap.set(key, value)
  })

  return (
    <div
      css={containerStyles}
      data-testid="overlays-wrapper"
      className="overlays-wrapper"
    >
      {Object.keys(overlaysMap).map((key) => (
        <Fragment key={key}>
          {!overlaysMap[key]?.ad_data && (
            <Overlay
              key={`${key}-overlay`}
              data={overlaysMap[key]}
              image={preloadedImages[key]}
              stageWidth={playerWidth}
              stageHeight={playerHeight}
              variables={variablesMap}
            />
          )}
          {overlaysMap[key]?.ad_data !== undefined &&
            overlaysMap[key].ad_data?.ad_unit_path && (
              <OverlayWrapper
                key={`${key}-ad`}
                data={overlaysMap[key]}
                stageWidth={playerWidth}
                stageHeight={playerHeight}
                variables={variablesMap}
                isClickable
              >
                <AdSlot
                  adUnitPath={overlaysMap[key].ad_data?.ad_unit_path || ''}
                  targeting={overlaysMap[key].ad_data?.ad_targeting}
                  slotRefreshRate={
                    overlaysMap[key].ad_data?.ad_slot_refresh_rate
                  }
                  sizeMapping={adSizing}
                />
              </OverlayWrapper>
            )}
        </Fragment>
      ))}
    </div>
  )
}

export default Overlays

const containerStyles: Interpolation<Theme> = {
  height: '100%',
  overflow: 'hidden',
  width: '100%',
  direction: 'ltr',
  position: 'relative',
}
